/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import FilterModal from "./FilterModal";
import IconComponent from "@CubeComponents/icons";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getConditionMet, callRemoteSource, type FilterType, type QueryOperator } from "../filterUtils";
import { useTranslation } from "@i18n";

/* --------------------------------------------
   Types
   -------------------------------------------- */

export interface FilterColumn {
  /** Unique field key */
  field: string;
  /** Display name for the column */
  name: string;
  /** Filter type: "Text" | "Date" | "Number" | "MultiSelect" | "FileSize" */
  filterType: string;
  /** Unique id (defaults to field if not provided) */
  id?: string;
  /** Disable filtering for this column */
  disableFiltering?: boolean;
}

interface RemoteDataSourceConfig {
  /** API endpoint URL or a function that performs the remote call */
  url: string | ((body: any) => Promise<any>);
  /** Base query object to send with the remote call (e.g. { params: { ... }, skip: 0, take: 50 }) */
  query?: any;
  /** Transform raw API response before it reaches the FilterModal (e.g. extract nested array) */
  beforeDataBound?: (data: any) => any;
  /** Transform filter conditions before they are passed to onFilterChange (e.g. build a where clause) */
  getUpdatedFilterConditions?: (filterConditionsObj: Record<string, any>) => any;
}

/** A single saved-view filter condition keyed by field in the conditions object */
export interface FilterConditionEntry {
  id?: string;
  type?: string;
  option?: string;
  text?: unknown;
  logic?: string;
}

/** A condition row as consumed by the filter modal */
interface FilterConditionRow {
  field?: string;
  operator?: string;
  value?: unknown;
  logic: string;
  id?: string;
}

const extractFilterIds = (obj: Record<string, FilterConditionEntry>): string[] =>
  Object.values(obj)
    .map((c) => c?.id)
    .filter((id): id is string => Boolean(id));

export interface FilterButtonProps {
  /** Column definitions available for filtering */
  columns: FilterColumn[];
  /** The local dataset to filter against. Used for autocomplete suggestions in local mode. */
  data?: any[];
  /** Called whenever filter conditions change. Receives the conditions object keyed by field. */
  onFilterChange: (filterConditionsObject: Record<string, any>) => void;
  /** Called when filter predicates change (remote mode). Receives predicates for building API where clauses. */
  onFilterPredicatesChange?: (predicates: any) => void;
  /** Optional: custom function to extract a field value from a data item */
  getFieldValue?: (item: any, field: string) => any;
  /** Optional: render a custom trigger instead of the default filter icon button */
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    hasActiveFilters: boolean;
  }) => React.ReactNode;
  /** Optional: remote data source config. When provided, filter suggestions are fetched from the API. */
  remoteDataSource?: RemoteDataSourceConfig;
  /** Optional: ref to the grid instance (needed for remote filtering to refresh data) */
  gridRef?: React.RefObject<any>;
  /** Optional: seed the derived filterConditionsObject (used to restore filters from a saved view) */
  initialFilterConditionsObject?: Record<string, FilterConditionEntry>;
}

/* --------------------------------------------
   Component
   -------------------------------------------- */

export default function FilterButton({
  columns,
  data = [],
  onFilterChange,
  onFilterPredicatesChange,
  getFieldValue,
  renderTrigger,
  remoteDataSource,
  gridRef: externalGridRef,
  initialFilterConditionsObject,
}: FilterButtonProps) {
  const { t } = useTranslation();

  const isRemote = !!remoteDataSource;

  const deriveConditionsFromObject = (obj?: Record<string, FilterConditionEntry>): FilterConditionRow[] => {
    if (!obj) return [];
    return Object.values(obj).map((c) => ({
      field: c?.id,
      operator: c?.option,
      value: c?.text,
      logic: c?.logic || "and",
      id: c?.id,
    }));
  };

  const [conditions, setConditions] = useState<any[]>(deriveConditionsFromObject(initialFilterConditionsObject));
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<string[]>(
    initialFilterConditionsObject ? extractFilterIds(initialFilterConditionsObject) : [],
  );
  const [filterConditionsObject, setFilterConditionsObject] = useState<Record<string, FilterConditionEntry>>(
    initialFilterConditionsObject ?? {},
  );
  const [filterPredicates, setFilterPredicates] = useState<unknown>("");
  const [tempCondition, setTempCondition] = useState<any>(null);
  const internalGridRef = useRef<any>({ current: null });
  const gridRef = externalGridRef || internalGridRef;
  const seededConditionsRef = useRef<Record<string, FilterConditionEntry> | null>(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    const obj = initialFilterConditionsObject ?? {};
    seededConditionsRef.current = obj;
    setFilterConditionsObject(obj);
    setConditions(deriveConditionsFromObject(initialFilterConditionsObject));
    setAppliedFilters(extractFilterIds(obj));
  }, [initialFilterConditionsObject]);

  // Propagate filter changes to the consumer
  useEffect(() => {
    if (seededConditionsRef.current === filterConditionsObject) {
      seededConditionsRef.current = null;
      return;
    }
    onFilterChangeRef.current(filterConditionsObject);
  }, [filterConditionsObject]);

  // Propagate filter predicates to the consumer (remote mode)
  useEffect(() => {
    if (isRemote && onFilterPredicatesChange) {
      onFilterPredicatesChange(filterPredicates);
    }
  }, [filterPredicates]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setTempCondition(null);
    setIsOpen(false);
  }, []);

  const columnDefinitions = useMemo(
    () =>
      columns.map((col) => ({
        field: col.field,
        name: col.name,
        filterType: col.filterType,
        id: col.id || col.field,
        disableFiltering: col.disableFiltering,
      })),
    [columns],
  );

  // Local apply filter function — reuses getConditionMet from filterUtils (same as SlickGrid1's applyFilter)
  const applyFilterFunction = useCallback(
    (dataset: any[], field: string, operator: string, text: string, filterType: string) => {
      return dataset.filter((item: any) => {
        const fieldValue = getFieldValue ? getFieldValue(item, field) : item[field];
        return getConditionMet(filterType as FilterType, fieldValue, operator as QueryOperator, text);
      });
    },
    [getFieldValue],
  );

  // Remote call handler — reuses callRemoteSource from filterUtils (same as SlickGrid1's callRemote)
  const callRemote = useCallback(
    async (_source: any, body: any) => {
      if (!remoteDataSource) return { result: [] };
      return callRemoteSource(remoteDataSource.url, body);
    },
    [remoteDataSource],
  );

  const hasActiveFilters = conditions.length > 0;

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: handleClick, hasActiveFilters })
      ) : (
        <ToolTipComponent content={t("Filtering")}>
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleClick} iconButton={true}>
            <IconComponent name="Filter" width="1.5rem" height="1.5rem" />
            {hasActiveFilters && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  insetInlineEnd: 0,
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "6.25rem",
                  background: "var(--Notification-notification-primary, #6BBBE3)",
                  transform: "translate(-50%, 50%)",
                  zIndex: 10,
                }}
              />
            )}
          </ButtonComponent>
        </ToolTipComponent>
      )}

      {isOpen && (
        <FilterModal
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          columnDefinitions={columnDefinitions as any}
          conditions={conditions}
          setConditions={setConditions}
          remoteDataSource={isRemote ? (remoteDataSource!.url as string) : null}
          callRemote={callRemote}
          beforeDataBound={remoteDataSource?.beforeDataBound || ((d: any) => d)}
          datasetForFetchingOptions={data}
          query={isRemote ? remoteDataSource!.query || null : null}
          localData={!isRemote}
          getUpdatedFilterConditions={
            remoteDataSource?.getUpdatedFilterConditions || ((obj: Record<string, any>) => obj)
          }
          applyFilterFunction={applyFilterFunction}
          gridRef={gridRef}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
          setFilterPredicates={setFilterPredicates}
          setFilterConditionsObject={setFilterConditionsObject}
          tempCondition={tempCondition}
          setTempCondition={setTempCondition}
        />
      )}
    </>
  );
}
